import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusOutlined,
  DeleteOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Form, DatePicker, Select, Input, Button, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import {
  EDUCATION_LEVEL_OPTIONS,
  RS_EDUCATION_LEVEL_OPTIONS,
} from '@/shared/employee';
import { DATE_FORMATS } from '@/utils/formUtils';
import EmployeeExperienceProfilePanel from './employee-general-data/sections/EmployeeExperienceProfilePanel';
import { formatSpan, workExperienceTotal } from './workExperience';
import EmployeeCareerTimeline, {
  EmployeeCareerTimelineHeader,
} from '@/components/employee/EmployeeCareerTimeline';

const GENDER_OPTIONS = [
  { value: 'male', key: 'male' },
  { value: 'female', key: 'female' },
  { value: 'other', key: 'other' },
];

const EMPLOYMENT_START_KEYS = ['employmentSettings', 'startDate'];
const EMPLOYMENT_END_KEYS = ['employmentSettings', 'endDate'];

const MARITAL_STATUS_OPTIONS = [
  { value: 'single', key: 'single' },
  { value: 'married', key: 'married' },
  { value: 'divorced', key: 'divorced' },
  { value: 'widowed', key: 'widowed' },
];

export default function EmployeeAdditionalPersonalInfoForm({
  form,
  className,
  onClose,
  open,
  onDirtyChange,
  resetTrigger,
  employeeId,
  market = 'RS',
  // Set on the profile page: the fields render inline and only those of the
  // selected tile are shown ('licno' = personal, 'staz' = work experience).
  inlineSection = null,
}) {
  const isInline = !!inlineSection;
  const sectionClass = (id) =>
    !isInline || inlineSection === id ? '' : 'hidden';
  const { t } = useTranslation(['forms', 'messages']);
  const [previousEmployments, setPreviousEmployments] = useState([]);
  const [children, setChildren] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const checkAndNotifyDirty = () => {
    if (!initialValues || !open || !onDirtyChange) return;

    const currentValues = form.getFieldValue('additionalPersonalInfo');
    const dirty =
      JSON.stringify(initialValues) !== JSON.stringify(currentValues);
    onDirtyChange(dirty);
  };

  useEffect(() => {
    checkAndNotifyDirty();
  }, [hasChanges]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (resetTrigger !== undefined) {
      const current = form.getFieldValue('additionalPersonalInfo');
      if (current) {
        setInitialValues(JSON.parse(JSON.stringify(current)));
      } else {
        setInitialValues(null);
      }
    }
  }, [resetTrigger, form]);

  const handleClose = async () => {
    try {
      // Validate only additionalPersonalInfo fields before closing
      await form.validateFields([
        ['additionalPersonalInfo', 'customId'],
        ['additionalPersonalInfo', 'birthDate'],
        ['additionalPersonalInfo', 'placeOfBirth'],
        ['additionalPersonalInfo', 'gender'],
        ['additionalPersonalInfo', 'familyCelebrationDate'],
        ['additionalPersonalInfo', 'previousEmployments'],
        ['additionalPersonalInfo', 'workExperience'],
        ['additionalPersonalInfo', 'workExperienceStartDate'],
        ['additionalPersonalInfo', 'maritalStatus'],
        ['additionalPersonalInfo', 'children'],
        ['additionalPersonalInfo', 'educationLevel'],
        ['additionalPersonalInfo', 'educationLevelOther'],
      ]);
      onClose();
    } catch {
      // Validation failed - don't close drawer
      // Error is already displayed on the field itself
    }
  };

  // Inline, the panel mounts before the parent fills the form, so the repeat
  // lists follow the form values instead of a one-off read on open.
  const watchedPreviousEmployments = Form.useWatch(
    ['additionalPersonalInfo', 'previousEmployments'],
    { form, preserve: true }
  );
  const watchedChildren = Form.useWatch(['additionalPersonalInfo', 'children'], {
    form,
    preserve: true,
  });
  useEffect(() => {
    if (!isInline) return;
    setPreviousEmployments(watchedPreviousEmployments || []);
    setChildren(watchedChildren || []);
  }, [isInline, watchedPreviousEmployments, watchedChildren]);

  useEffect(() => {
    if (open && !isInline) {
      const current = form.getFieldValue('additionalPersonalInfo');
      if (!current) {
        const emptyData = {
          previousEmployments: [],
          children: [],
        };
        form.setFieldsValue({
          additionalPersonalInfo: emptyData,
        });
        setPreviousEmployments([]);
        setChildren([]);
        // Set initial values only if not already set
        if (!initialValues) {
          setInitialValues(emptyData);
        }
      } else {
        setPreviousEmployments(current.previousEmployments || []);
        setChildren(current.children || []);
        // Set initial values only if not already set
        if (!initialValues) {
          setInitialValues(JSON.parse(JSON.stringify(current)));
        }
      }
    }
  }, [form, open, initialValues, isInline]);

  const handleFieldChange = () => {
    setHasChanges((prev) => !prev);
    setTimeout(() => checkAndNotifyDirty(), 0);
  };

  const handleAddPreviousEmployment = () => {
    const current = form.getFieldValue('additionalPersonalInfo') || {};
    const updatedEmployments = [
      ...(current.previousEmployments || []),
      { company: '', startDate: null, endDate: null },
    ];
    form.setFieldsValue({
      additionalPersonalInfo: {
        ...current,
        previousEmployments: updatedEmployments,
      },
    });
    setPreviousEmployments(updatedEmployments);
    handleFieldChange();
  };

  const handleRemovePreviousEmployment = (index) => {
    const current = form.getFieldValue('additionalPersonalInfo') || {};
    const currentEmployments = [...(current.previousEmployments || [])];
    currentEmployments.splice(index, 1);
    form.setFieldsValue({
      additionalPersonalInfo: {
        ...current,
        previousEmployments: currentEmployments,
      },
    });
    setPreviousEmployments(currentEmployments);
    handleFieldChange();
  };

  const handleAddChild = () => {
    const current = form.getFieldValue('additionalPersonalInfo') || {};
    const updatedChildren = [
      ...(current.children || []),
      { name: '', birthDate: null },
    ];
    form.setFieldsValue({
      additionalPersonalInfo: {
        ...current,
        children: updatedChildren,
      },
    });
    setChildren(updatedChildren);
    handleFieldChange();
  };

  const handleRemoveChild = (index) => {
    const current = form.getFieldValue('additionalPersonalInfo') || {};
    const currentChildren = [...(current.children || [])];
    currentChildren.splice(index, 1);
    form.setFieldsValue({
      additionalPersonalInfo: {
        ...current,
        children: currentChildren,
      },
    });
    setChildren(currentChildren);
    handleFieldChange();
  };

  const handleWorkExperienceChange = () => {
    // Check if all work experience fields are empty
    setTimeout(() => {
      const workExp = form.getFieldValue([
        'additionalPersonalInfo',
        'workExperience',
      ]);
      const hasWorkExp = workExp?.years || workExp?.months || workExp?.days;

      if (!hasWorkExp) {
        // Clear workExperienceStartDate if no work experience is entered
        form.setFieldsValue({
          additionalPersonalInfo: {
            ...form.getFieldValue('additionalPersonalInfo'),
            workExperienceStartDate: undefined,
          },
        });
      }
    }, 0);
  };

  const genderOptions = GENDER_OPTIONS.map(({ key, value }) => ({
    value,
    label: t(`formValidation.${key}`, { ns: 'forms' }),
  }));

  const maritalStatusOptions = MARITAL_STATUS_OPTIONS.map(({ key, value }) => ({
    value,
    label: t(`formValidation.${key}`, { ns: 'forms' }),
  }));

  // Serbian companies pick the national degree (I–VIII). A value entered
  // before that stays listed until someone picks a degree instead.
  const educationLevel = Form.useWatch(
    ['additionalPersonalInfo', 'educationLevel'],
    form
  );
  const educationLevelOptions = (() => {
    if (market !== 'RS') return EDUCATION_LEVEL_OPTIONS;
    const legacy = EDUCATION_LEVEL_OPTIONS.find(
      (option) => option.value === educationLevel
    );
    return legacy
      ? [...RS_EDUCATION_LEVEL_OPTIONS, legacy]
      : RS_EDUCATION_LEVEL_OPTIONS;
  })().map(({ key, value }) => ({
    value,
    label: t(`educationLevels.${key}`, { ns: 'forms' }),
  }));

  return (
    <div className={cn(!isInline && 'p-4', className)}>
      {employeeId && !isInline && (
        <section className="mb-6 grid gap-3 rounded-md border border-zinc-200 bg-zinc-50/50 p-3">
          <EmployeeCareerTimelineHeader />
          <EmployeeCareerTimeline employeeId={employeeId} compact />
        </section>
      )}

      <div className="grid gap-4">
        {/* On the profile: EmployeePersonalProfilePanel. */}
        {!isInline && (
        <div className="grid gap-4">
        <Form.Item
          name={['additionalPersonalInfo', 'customId']}
          label={t('formValidation.customId', { ns: 'forms' })}
        >
          <Input
            placeholder={t('formValidation.customId', { ns: 'forms' })}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        <Form.Item
          name={['additionalPersonalInfo', 'birthDate']}
          label={t('formValidation.birthDate', { ns: 'forms' })}
        >
          <DatePicker
            className="w-full"
            format={DATE_FORMATS.DISPLAY}
            placeholder={t('formValidation.birthDate', { ns: 'forms' })}
            defaultPickerValue={dayjs('1980-01-01')}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        <Form.Item
          name={['additionalPersonalInfo', 'placeOfBirth']}
          label={t('formValidation.placeOfBirth', { ns: 'forms' })}
        >
          <Input
            placeholder={t('formValidation.placeOfBirth', { ns: 'forms' })}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        <Form.Item
          name={['additionalPersonalInfo', 'gender']}
          label={t('formValidation.gender', { ns: 'forms' })}
        >
          <Select
            allowClear
            placeholder={t('formValidation.gender', { ns: 'forms' })}
            options={genderOptions}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        <Form.Item
          name={['additionalPersonalInfo', 'familyCelebrationDate']}
          label={t('formValidation.familyCelebrationDate', { ns: 'forms' })}
        >
          <DatePicker
            className="w-full"
            format="DD.MM"
            picker="date"
            placeholder={t('formValidation.familyCelebrationDate', {
              ns: 'forms',
            })}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>
        </div>
        )}

        {!isInline && <hr className="my-2" />}

        {isInline ? (
          <div className={sectionClass('staz')}>
            <EmployeeExperienceProfilePanel
              previousEmployments={previousEmployments}
              onAddEmployment={handleAddPreviousEmployment}
              onRemoveEmployment={handleRemovePreviousEmployment}
              onFieldChange={() => setHasChanges((prev) => !prev)}
              onExperienceChange={handleWorkExperienceChange}
            />
          </div>
        ) : (
          <>
        <div className={sectionClass('staz')}>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-base font-semibold">
              {t('formValidation.previousEmployments', { ns: 'forms' })}
            </h4>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddPreviousEmployment}
              size="small"
            >
              {t('formValidation.add', { ns: 'forms' })}
            </Button>
          </div>

          {previousEmployments &&
            previousEmployments.map((_, index) => (
              <div
                key={index}
                className="mb-4 rounded border border-zinc-200 bg-zinc-50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {t('formValidation.employment', { ns: 'forms' })}{' '}
                    {index + 1}
                  </span>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemovePreviousEmployment(index)}
                    size="small"
                  />
                </div>

                <Form.Item
                  name={[
                    'additionalPersonalInfo',
                    'previousEmployments',
                    index,
                    'company',
                  ]}
                  label={t('company', { ns: 'forms' })}
                  className="!mb-2"
                >
                  <Input
                    placeholder={t('company', { ns: 'forms' })}
                    onChange={() => setHasChanges((prev) => !prev)}
                  />
                </Form.Item>

                <div className="grid grid-cols-2 gap-2">
                  <Form.Item
                    name={[
                      'additionalPersonalInfo',
                      'previousEmployments',
                      index,
                      'startDate',
                    ]}
                    label={t('startDate', { ns: 'forms' })}
                    className="!mb-0"
                  >
                    <DatePicker
                      className="w-full"
                      format={DATE_FORMATS.DISPLAY}
                      placeholder={t('startDate', { ns: 'forms' })}
                      onChange={() => setHasChanges((prev) => !prev)}
                    />
                  </Form.Item>

                  <Form.Item
                    name={[
                      'additionalPersonalInfo',
                      'previousEmployments',
                      index,
                      'endDate',
                    ]}
                    label={t('endDate', { ns: 'forms' })}
                    className="!mb-0"
                  >
                    <DatePicker
                      className="w-full"
                      format={DATE_FORMATS.DISPLAY}
                      placeholder={t('endDate', { ns: 'forms' })}
                      onChange={() => setHasChanges((prev) => !prev)}
                    />
                  </Form.Item>
                </div>
              </div>
            ))}
        </div>

        <hr className={cn('my-2', sectionClass('staz'))} />

        <div className={sectionClass('staz')}>
          <label className="mb-2 block text-sm font-medium">
            {t('formValidation.workExperience', { ns: 'forms' })}{' '}
            <Tooltip
              title={t('formValidation.workExperienceTooltip', { ns: 'forms' })}
            >
              <QuestionCircleOutlined className="text-gray-400" />
            </Tooltip>
          </label>
          <p className="mb-2 text-xs text-gray-500">
            {t('formValidation.workExperienceHelp', { ns: 'forms' })}
          </p>
          <div className="grid grid-cols-3 gap-2">
            <Form.Item
              name={['additionalPersonalInfo', 'workExperience', 'years']}
              className="!mb-0"
            >
              <Input
                type="number"
                min={0}
                placeholder={t('formValidation.years', { ns: 'forms' })}
                suffix={t('formValidation.years', { ns: 'forms' })}
                onChange={() => {
                  handleWorkExperienceChange();
                  setHasChanges((prev) => !prev);
                }}
              />
            </Form.Item>
            <Form.Item
              name={['additionalPersonalInfo', 'workExperience', 'months']}
              className="!mb-0"
            >
              <Input
                type="number"
                min={0}
                max={11}
                placeholder={t('formValidation.months', { ns: 'forms' })}
                suffix={t('formValidation.months', { ns: 'forms' })}
                onChange={() => {
                  handleWorkExperienceChange();
                  setHasChanges((prev) => !prev);
                }}
              />
            </Form.Item>
            <Form.Item
              name={['additionalPersonalInfo', 'workExperience', 'days']}
              className="!mb-0"
            >
              <Input
                type="number"
                min={0}
                max={30}
                placeholder={t('formValidation.days', { ns: 'forms' })}
                suffix={t('formValidation.days', { ns: 'forms' })}
                onChange={() => {
                  handleWorkExperienceChange();
                  setHasChanges((prev) => !prev);
                }}
              />
            </Form.Item>
          </div>

          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) =>
              prev?.additionalPersonalInfo?.workExperience !==
              cur?.additionalPersonalInfo?.workExperience
            }
          >
            {({ getFieldValue }) => {
              const workExp = getFieldValue([
                'additionalPersonalInfo',
                'workExperience',
              ]);
              const hasWorkExp =
                workExp?.years || workExp?.months || workExp?.days;

              if (!hasWorkExp) {
                return null;
              }

              const employmentStart = getFieldValue(EMPLOYMENT_START_KEYS);
              return (
                <Form.Item
                  name={['additionalPersonalInfo', 'workExperienceStartDate']}
                  label={t('formValidation.workExperienceStartDate', {
                    ns: 'forms',
                  })}
                  className="mt-3"
                  // Optional: left empty, counting starts with the employment
                  // start, so the time in the company always counts.
                  extra={
                    employmentStart
                      ? t('employeeProfile.experienceUi.countingDefault', {
                          ns: 'translations',
                          date: dayjs(employmentStart).format(
                            DATE_FORMATS.DISPLAY
                          ),
                        })
                      : undefined
                  }
                >
                  <DatePicker
                    className="w-full"
                    format={DATE_FORMATS.DISPLAY}
                    placeholder={t(
                      'formValidation.workExperienceStartDatePlaceholder',
                      { ns: 'forms' }
                    )}
                    onChange={() => setHasChanges((prev) => !prev)}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              prevValues?.additionalPersonalInfo?.workExperience !==
                curValues?.additionalPersonalInfo?.workExperience ||
              prevValues?.additionalPersonalInfo?.workExperienceStartDate !==
                curValues?.additionalPersonalInfo?.workExperienceStartDate ||
              prevValues?.employmentSettings?.startDate !==
                curValues?.employmentSettings?.startDate ||
              prevValues?.employmentSettings?.endDate !==
                curValues?.employmentSettings?.endDate
            }
            noStyle
          >
            {({ getFieldValue }) => {
              // Same rules as the profile's work experience tile.
              const exp = workExperienceTotal(
                getFieldValue(['additionalPersonalInfo', 'workExperience']),
                getFieldValue([
                  'additionalPersonalInfo',
                  'workExperienceStartDate',
                ]),
                getFieldValue(EMPLOYMENT_END_KEYS),
                getFieldValue(EMPLOYMENT_START_KEYS)
              );
              if (!exp?.from) return null;

              return (
                <div className="mt-3 rounded-md bg-blue-50 p-3">
                  <div className="text-sm font-medium text-blue-900">
                    {t('formValidation.totalWorkExperience', { ns: 'forms' })}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-blue-700">
                    {formatSpan(exp.total, t)}
                  </div>
                  <div className="mt-1 text-xs text-blue-600">
                    {t('formValidation.calculatedFrom', { ns: 'forms' })}{' '}
                    {exp.from.format(DATE_FORMATS.DISPLAY)}
                  </div>
                  {exp.inFirm && (
                    <div className="mt-1 text-xs text-blue-600">
                      {t('employeeProfile.experienceUi.inFirmShort', {
                        ns: 'translations',
                        span: formatSpan(exp.inFirm, t),
                      })}
                    </div>
                  )}
                </div>
              );
            }}
          </Form.Item>
        </div>
          </>
        )}

        {!isInline && <hr className="my-2" />}

        {/* On the profile: EmployeePersonalProfilePanel. */}
        {!isInline && (
        <div className="grid gap-4">
        <Form.Item
          name={['additionalPersonalInfo', 'maritalStatus']}
          label={t('formValidation.maritalStatus', { ns: 'forms' })}
        >
          <Select
            allowClear
            placeholder={t('formValidation.maritalStatus', { ns: 'forms' })}
            options={maritalStatusOptions}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        <Form.Item
          name={['additionalPersonalInfo', 'educationLevel']}
          label={t('educationLevel', { ns: 'forms' })}
        >
          <Select
            allowClear
            placeholder={t('educationLevel', { ns: 'forms' })}
            options={educationLevelOptions}
            onChange={() => setHasChanges((prev) => !prev)}
          />
        </Form.Item>

        {educationLevel === 'other' && (
          <Form.Item
            name={['additionalPersonalInfo', 'educationLevelOther']}
            label={t('educationLevelOther', { ns: 'forms' })}
            tooltip={t('educationLevelOtherHint', { ns: 'forms' })}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('educationLevelOtherRequired', { ns: 'forms' }),
              },
            ]}
          >
            <Input
              placeholder={t('educationLevelOtherPlaceholder', { ns: 'forms' })}
              onChange={() => setHasChanges((prev) => !prev)}
            />
          </Form.Item>
        )}

        <div>
          <div className="mb-3 flex items-center justify-between">
            <h4 className="text-base font-semibold">
              {t('formValidation.children', { ns: 'forms' })}
            </h4>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={handleAddChild}
              size="small"
            >
              {t('formValidation.add', { ns: 'forms' })}
            </Button>
          </div>

          {children &&
            children.map((_, index) => (
              <div
                key={index}
                className="mb-4 rounded border border-zinc-200 bg-zinc-50 p-3"
              >
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {t('formValidation.child', { ns: 'forms' })} {index + 1}
                  </span>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveChild(index)}
                    size="small"
                  />
                </div>

                <Form.Item
                  name={['additionalPersonalInfo', 'children', index, 'name']}
                  label={t('formValidation.childName', { ns: 'forms' })}
                  className="!mb-2"
                >
                  <Input
                    placeholder={t('formValidation.childName', { ns: 'forms' })}
                    onChange={() => setHasChanges((prev) => !prev)}
                  />
                </Form.Item>

                <Form.Item
                  name={[
                    'additionalPersonalInfo',
                    'children',
                    index,
                    'birthDate',
                  ]}
                  label={t('formValidation.birthDate', { ns: 'forms' })}
                  className="!mb-0"
                >
                  <DatePicker
                    className="w-full"
                    format={DATE_FORMATS.DISPLAY}
                    placeholder={t('formValidation.birthDate', { ns: 'forms' })}
                    onChange={() => setHasChanges((prev) => !prev)}
                  />
                </Form.Item>
              </div>
            ))}
        </div>
        </div>
        )}
      </div>

      {!isInline && (
        <div className="form-footer form-footer--sticky">
          <Button
            type="primary"
            className="btn-primary w-full"
            onClick={handleClose}
          >
            {t('formValidation.close', { ns: 'forms' })}
          </Button>
        </div>
      )}
    </div>
  );
}
